/**
 * Receiving a model's box by its colour × size table (1.34). The rules live
 * here so they can be tested; the dialog only wires them to the screen. The
 * money is create_purchase's: one transaction writes the purchase, its lines,
 * the stock at the branch, the weighted cost and the supplier's balance.
 */
import { paidSourceError, type PaidSource } from './paid-source'
import { roundMoney } from './money-round'
import { isMeasure, roundWeight } from './weight'
import type { Key } from './i18n'
import { productCodes } from './barcodes'
import { unitMargin } from './profit'
import { blendCost } from './utils'
import { compareSizes, isStoreCode, variantKey } from './variants'

/** One cell of the table: a size the model has, or one this receipt makes. */
export type ReceiveCell = {
  key: string
  size: string
  color: string
  /** null = this colour × size does not exist yet; it is made at 0 first */
  product: { id: string; name: string; barcode: string | null; extra_barcodes?: string[]; quantity: number } | null
}

export type ReceiveField = 'cost' | 'cells' | 'paid' | 'source'

export type ReceiveLine = { product_id: string; product_name: string; barcode: string | null; quantity: number; unit_cost: number; total_cost: number }

/** What a cell needs from a size — any Product fits. */
type Variantish = { id: string; name: string; barcode: string | null; extra_barcodes?: string[]; quantity: number; size?: string | null; color?: string | null }

const clean = (s: string | null | undefined) => (s ?? '').trim().replace(/\s+/g, ' ')
const has = (list: string[], v: string) => list.some((x) => x.toLowerCase() === v.toLowerCase())
const round2 = (n: number) => Math.round(n * 100) / 100
const asProduct = (p: Variantish) => ({ id: p.id, name: p.name, barcode: p.barcode, extra_barcodes: p.extra_barcodes, quantity: p.quantity })

/**
 * The table: the model's colours in its own order and its sizes sorted, plus
 * the ones typed for this box (case and spaces aside, as the database's key).
 * A model without colours takes none here — its colourless row would vanish
 * under the new one — and one without sizes takes no size; the model dialog
 * does that.
 */
export function receiveGrid(
  model: { colors: string[]; sizes: string[]; variants: Variantish[] },
  extra: { sizes: string[]; colors: string[] },
): { colors: string[]; sizes: string[]; cells: ReceiveCell[] } {
  const colors = [...model.colors]
  if (model.colors.length > 0) for (const c of extra.colors) if (clean(c) && !has(colors, clean(c))) colors.push(clean(c))
  const sizes = [...model.sizes]
  if (model.sizes.length > 0) for (const s of extra.sizes) if (clean(s) && !has(sizes, clean(s))) sizes.push(clean(s))
  sizes.sort(compareSizes)
  const byKey = new Map(model.variants.map((p) => [variantKey(p.size, p.color), p]))
  const rows = colors.length ? colors : ['']
  const cols = sizes.length ? sizes : ['']
  const cells = rows.flatMap((color) => cols.map((size) => {
    const key = variantKey(size, color)
    const p = byKey.get(key)
    return { key, size, color, product: p ? asProduct(p) : null }
  }))
  return { colors, sizes, cells }
}

/** The most one cell may receive: a box, not a scanner's 13 digits. */
export const MAX_CELL = 9999

/** A typed count: empty or 0 → 0; a whole number above 0 → it; anything else → NaN (refused). Pieces, not kilos. */
export function cellCount(typed: string | undefined): number {
  const s = (typed ?? '').trim()
  if (s === '') return 0
  const n = Number(s)
  if (n === 0) return 0
  return Number.isInteger(n) && n > 0 && n <= MAX_CELL ? n : NaN
}

/** The cost the field opens with: the model's, when every size has the same one. */
export function presetCost(variants: { purchase_price: number }[]): string {
  const costs = new Set(variants.map((p) => p.purchase_price))
  return costs.size === 1 ? String([...costs][0]) : ''
}

/**
 * The hint under the cost. Sizes with different costs are compared with the
 * highest („dearer than anything on the shelf"); the margin is taken at the
 * lowest sale price — the size that loses most. unitMargin, as everywhere.
 */
export function costCheck(cost: number, variants: { purchase_price: number; sale_price: number }[]): { current: number; up: boolean; sale: number; marginPct: number | null } {
  const current = variants.length ? Math.max(...variants.map((p) => p.purchase_price)) : 0
  const sale = variants.length ? Math.min(...variants.map((p) => p.sale_price)) : 0
  const m = unitMargin(sale, cost)
  return { current, up: current > 0 && cost > current * 1.005, sale, marginPct: m == null ? null : Math.round(m * 100) }
}

/** The first rule the form breaks: the cost, a count that is not whole, nothing counted, the paid amount. */
export function firstReceiveError(f: {
  cost: string; counts: number[]; paid: string; hasSupplier: boolean; total: number
  /** 171 — where the paid money came from; asked only by a caller that passes it */
  source?: PaidSource | null; hasShift?: boolean
}): { field: ReceiveField; key: Key } | null {
  const cost = f.cost.trim() === '' ? NaN : Number(f.cost)
  if (!(Number.isFinite(cost) && cost >= 0)) return { field: 'cost', key: 'wh.rcv.errCost' }
  if (f.counts.some((n) => Number.isNaN(n))) return { field: 'cells', key: 'wh.rcv.errWhole' }
  if (!f.counts.some((n) => n > 0)) return { field: 'cells', key: 'wh.rcv.errEmpty' }
  const paid = paidError(f.paid, f.total, f.hasSupplier)
  if (paid) return { field: 'paid', key: paid }
  if (f.source === undefined) return null
  const src = paidSourceError(paidFor(f.total, f.paid, f.hasSupplier), f.source, f.hasShift === true)
  return src ? { field: 'source', key: src } : null
}

/**
 * The paid field of any purchase form. Without a supplier there is no field —
 * nobody could ever be paid the rest (1.53: the old purchase dialog let a
 * debt be saved with no supplier, and it could never be cleared). With one:
 * empty = everything, else a number from 0 to the total.
 */
export function paidError(typed: string, total: number, hasSupplier: boolean): Key | null {
  if (!hasSupplier || typed.trim() === '') return null
  const p = Number(typed)
  if (!(Number.isFinite(p) && p >= 0)) return 'wh.rcv.errPaid'
  if (round2(p) > round2(total)) return 'wh.rcv.errPaidOver'
  return null
}

/** What is paid now: everything without a supplier or with the field empty; else the typed amount, to the cent. */
export function paidFor(total: number, typed: string, hasSupplier: boolean): number {
  if (!hasSupplier || typed.trim() === '') return total
  return round2(Number(typed))
}

/**
 * create_purchase's lines: one per counted cell that exists, in product-id
 * order — the order restock_products (133) takes rows in, so a till selling
 * the same sizes cannot deadlock with it. create_purchase takes the client's
 * total, so it is summed here, to the cent.
 */
export function receiveLines(cells: ReceiveCell[], count: (key: string) => number, cost: number): { lines: ReceiveLine[]; units: number; total: number } {
  const lines = cells
    .filter((c) => c.product && count(c.key) > 0)
    .map((c) => ({
      product_id: c.product!.id, product_name: c.product!.name, barcode: c.product!.barcode,
      quantity: count(c.key), unit_cost: cost, total_cost: round2(count(c.key) * cost),
    }))
    .sort((a, b) => (a.product_id < b.product_id ? -1 : a.product_id > b.product_id ? 1 : 0))
  const units = lines.reduce((s, l) => s + l.quantity, 0)
  return { lines, units, total: round2(units * cost) }
}

/** Counted cells the model does not have yet — made at 0 before the purchase. */
export function cellsToCreate(cells: ReceiveCell[], count: (key: string) => number): ReceiveCell[] {
  return cells.filter((c) => !c.product && count(c.key) > 0)
}

/** The cells with the sizes just made — a second press after a failed purchase makes nothing twice. */
export function withProducts(cells: ReceiveCell[], made: Variantish[]): ReceiveCell[] {
  const byKey = new Map(made.map((p) => [variantKey(p.size, p.color), p]))
  return cells.map((c) => {
    if (c.product) return c
    const p = byKey.get(c.key)
    return p ? { ...c, product: asProduct(p) } : c
  })
}

/** A size needs a printed label unless it carries the supplier's code. A size not made yet gets the shop's. */
export const needsLabel = (c: ReceiveCell) => !c.product || !c.product.barcode?.trim() || isStoreCode(c.product.barcode)

/** One label per RECEIVED unit, only where a label is needed; `byProduct` has the made cells only. */
export function receiveLabels(cells: ReceiveCell[], count: (key: string) => number): { units: number; byProduct: Record<string, number> } {
  let units = 0
  const byProduct: Record<string, number> = {}
  for (const c of cells) {
    const n = count(c.key)
    if (n > 0 && needsLabel(c)) {
      units += n
      if (c.product) byProduct[c.product.id] = n
    }
  }
  return { units, byProduct }
}

/** What a colour's row says under its name: all its sizes carry supplier codes, or labels print (a counted „ახალი" cell prints one). */
export function rowNote(cells: ReceiveCell[], color: string, count: (key: string) => number): 'supplier' | 'label' {
  const mine = cells.filter((c) => c.color.toLowerCase() === color.toLowerCase() && (c.product || count(c.key) > 0))
  return mine.length > 0 && mine.every((c) => !needsLabel(c)) ? 'supplier' : 'label'
}

/**
 * „ყველა უჯრაში": the sizes the model has and the ones added for this box —
 * never a hole (a colour × size the shop never stocked), which would be made.
 */
export function fillTargets(cells: ReceiveCell[], extra: { sizes: string[]; colors: string[] }): string[] {
  return cells
    .filter((c) => c.product || has(extra.sizes.map(clean), c.size) || has(extra.colors.map(clean), c.color))
    .map((c) => c.key)
}

/** The cell a scanned code belongs to (a size of this model), or null. */
export function cellForCode(cells: ReceiveCell[], code: string): ReceiveCell | null {
  const k = code.trim()
  return k ? cells.find((c) => !!c.product && productCodes(c.product).includes(k)) ?? null : null
}

/**
 * The gun types into whatever has focus before the scanner hook sees the Enter
 * (it stops only the Enter), at the caret or over a selection: a cell holding
 * „2" can read „22900000000011" or „29000000000112". Take the first occurrence
 * of the code back out; anything else is left alone.
 */
export function stripScan(value: string, code: string): string {
  const i = code ? value.indexOf(code) : -1
  return i < 0 ? value : value.slice(0, i) + value.slice(i + code.length)
}

/**
 * A booked receipt on the screen copy, the way create_purchase books it: line
 * after line, each blending the cost against what the org holds by then (its
 * stock trigger raises products.quantity after every line). Stock rises at the
 * active branch (`quantity`, already lensed to it) and, in a multi-branch copy,
 * in `org_quantity` too; the cost is org-wide. One product on two lines counts
 * both (Codex final review #4: the old `find` counted the first).
 */
export function receiptOnProducts<P extends { id: string; quantity: number; org_quantity?: number; purchase_price: number }>(
  products: P[],
  lines: { product_id: string | null; quantity: number; unit_cost: number }[],
): P[] {
  return products.map((p) => {
    const mine = lines.filter((l) => l.product_id === p.id)
    if (mine.length === 0) return p
    let qty = p.quantity
    let orgQty = p.org_quantity ?? p.quantity
    let cost = p.purchase_price
    for (const l of mine) {
      cost = blendCost(orgQty, cost, l.quantity, l.unit_cost)
      orgQty = roundWeight(orgQty + l.quantity)
      qty = roundWeight(qty + l.quantity)
    }
    return { ...p, quantity: qty, ...(p.org_quantity !== undefined ? { org_quantity: orgQty } : {}), purchase_price: cost }
  })
}

/*
 * 1.72 — the supplier purchase window by weight. Its quantity field read whole
 * numbers only, so a chicken shop's 12.5 kg delivery was booked as 12 and 0.8 kg as 1:
 * the shelf, the cost and the supplier's debt all short, and the batch's last half
 * kilo refused at the till.
 */

/** A weighed line waits for its weight (a forgotten one must not book 1 kg); a piece line starts at one. */
export const purchaseStartQty = (unit: string | null | undefined): number => (isMeasure(unit) ? 0 : 1)

/** The same product picked or scanned again: one more piece — but never one more kilo. */
export const purchaseQtyAgain = (qty: number, unit: string | null | undefined): number => (isMeasure(unit) ? qty : qty + 1)

/** A line's money to the tetri, so the lines add up to the total on the screen and in the supplier's debt. */
export const purchaseLineMoney = (qty: number, cost: number): number => roundMoney(qty * cost)

export const purchaseMoney = (lines: readonly { quantity: number; unit_cost: number }[]): number =>
  roundMoney(lines.reduce((s, l) => s + purchaseLineMoney(l.quantity, l.unit_cost), 0))

export const purchaseCount = (lines: readonly { quantity: number }[]): number =>
  roundWeight(lines.reduce((s, l) => s + l.quantity, 0))

/** The lines the save must refuse: a weight never typed. */
export const linesWithoutQty = <L extends { quantity: number }>(lines: readonly L[]): L[] => lines.filter((l) => !(l.quantity > 0))
